import { useCallback, useEffect, useState } from 'react';
import { equalTo, get, orderByChild, query, ref } from 'firebase/database';
import { database } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

// Normalize an email address into a Firebase-safe key (matching Profile.js convention)
const emailToKey = (email = '') =>
  String(email || '').trim().toLowerCase().replace(/[.#$/[\]]/g, '_');

// Find a profile overlay by uid or email key
const getProfileOverlay = (profiles = {}, uid = '', email = '') => {
  if (uid && profiles[uid]) return profiles[uid];
  if (email) {
    const key = emailToKey(email);
    if (profiles[key]) return profiles[key];
  }
  return {};
};

/**
 * Loads ALL team members for a company, matching the full logic used in Profile.js:
 *   - collaborators stored with `companyId` (new system)
 *   - collaborators stored with `company_uid` (legacy system)
 *   - teamAccessProfiles (enriched profile data / profile-only entries)
 *   - pendingInvites (people invited but not yet signed up)
 *   - company owner (resolved via Firebase Auth when current user is the owner,
 *     or via the `ownerEmail` field on the company document for collaborators)
 *
 * Returns:
 *   teamMembers   – active/accepted members (owner + collaborators + profile-only entries)
 *   pendingInvites – invited but not yet accepted
 *   loading
 *   reload        – call to force a fresh fetch
 *
 * Each member shape:
 *   { uid, email, name, firstName, lastName, position, role, department,
 *     departments, departmentScope, permissions, isOwner, sourceType }
 */
export default function useTeamMembers(companyUID) {
  const { currentUser } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!companyUID) return;
    setLoading(true);
    try {
      const newCollabQ = query(
        ref(database, 'collaborators'),
        orderByChild('companyId'),
        equalTo(companyUID)
      );
      const legacyCollabQ = query(
        ref(database, 'collaborators'),
        orderByChild('company_uid'),
        equalTo(companyUID)
      );

      const [newSnap, legacySnap, invitesSnap, profilesSnap, companySnap] = await Promise.all([
        get(newCollabQ),
        get(legacyCollabQ),
        get(ref(database, `companies/${companyUID}/pendingInvites`)),
        get(ref(database, `companies/${companyUID}/teamAccessProfiles`)),
        get(ref(database, `companies/${companyUID}`)),
      ]);

      const profiles = profilesSnap.exists() ? profilesSnap.val() || {} : {};
      const members = [];
      const invites = [];
      const seenUIDs = new Set();
      const seenEmails = new Set();

      const buildMember = (uid, data, overlay, sourceType) => {
        const email = String(overlay.email || data.email || '').trim().toLowerCase();
        const name =
          overlay.name ||
          data.name ||
          data.displayName ||
          (email ? email.split('@')[0] : '') ||
          '';
        const primaryDept = data.departments
          ? Object.keys(data.departments)[0]
          : overlay.departments
          ? Object.keys(overlay.departments)[0]
          : null;
        return {
          uid,
          email,
          name,
          firstName: overlay.firstName || data.firstName || '',
          lastName: overlay.lastName || data.lastName || '',
          position: overlay.position || data.position || data.role || '',
          role: overlay.position || data.position || data.role || 'Team Member',
          department: primaryDept || data.department || overlay.department || 'HR',
          departments: overlay.departments || data.departments || {},
          departmentScope: overlay.departmentScope || data.departmentScope || 'all',
          permissions: overlay.permissions || data.permissions || {},
          isOwner: false,
          sourceType,
        };
      };

      const addCollaborator = (child) => {
        if (seenUIDs.has(child.key)) return;
        seenUIDs.add(child.key);
        const data = child.val() || {};
        const overlay = getProfileOverlay(profiles, child.key, data.email);
        const member = buildMember(child.key, data, overlay, 'collaborator');
        if (member.email) seenEmails.add(member.email);
        members.push(member);
      };

      if (newSnap.exists()) newSnap.forEach(addCollaborator);
      if (legacySnap.exists()) legacySnap.forEach(addCollaborator);

      // Profiles not yet in the collaborators node
      Object.entries(profiles).forEach(([profileKey, profileVal]) => {
        const uid = profileVal?.uid || profileKey;
        const email = String(profileVal?.email || '').trim().toLowerCase();
        if (seenUIDs.has(uid)) return;
        if (email && seenEmails.has(email)) return;
        const member = buildMember(uid, {}, profileVal || {}, 'profile');
        if (uid) seenUIDs.add(uid);
        if (email) seenEmails.add(email);
        members.push(member);
      });

      // Pending invites
      if (invitesSnap.exists()) {
        invitesSnap.forEach((child) => {
          const data = child.val() || {};
          const overlay = getProfileOverlay(profiles, child.key, data.email);
          const invite = buildMember(child.key, data, overlay, 'invite');
          invite.isOwner = false;
          invite.role = invite.role || 'Invited';
          invites.push(invite);
        });
      }

      // Company owner – resolved from company document or Firebase Auth.
      // When the current user IS the owner (uid matches), their email comes from
      // Firebase Auth directly — no need for it to be stored on the company doc.
      // When a collaborator is loading the list, ownerEmail must be on the doc.
      if (companySnap.exists()) {
        const cd = companySnap.val() || {};
        let ownerEmail = String(cd.ownerEmail || cd.contactEmail || cd.email || '').trim().toLowerCase();
        if (!ownerEmail && currentUser?.uid === companyUID && currentUser?.email) {
          ownerEmail = currentUser.email.toLowerCase();
        }
        if (ownerEmail && !seenEmails.has(ownerEmail)) {
          const ownerDisplayName =
            cd.ownerName ||
            (currentUser?.uid === companyUID ? currentUser?.displayName : null) ||
            cd.contactName ||
            cd.companyName ||
            ownerEmail.split('@')[0];
          members.unshift({
            uid: companyUID,
            email: ownerEmail,
            name: ownerDisplayName,
            firstName: cd.ownerFirstName || '',
            lastName: cd.ownerLastName || '',
            position: 'Owner',
            role: 'Owner',
            department: 'HR',
            departments: {},
            departmentScope: 'all',
            permissions: {},
            isOwner: true,
            sourceType: 'owner',
          });
        }
      }

      setTeamMembers(members);
      setPendingInvites(invites);
    } catch (err) {
      console.error('useTeamMembers: failed to load team members', err);
    } finally {
      setLoading(false);
    }
  }, [companyUID, currentUser]);

  useEffect(() => {
    load();
  }, [load]);

  return { teamMembers, pendingInvites, loading, reload: load };
}
